/**
 * CountyRequirements Component
 * Display local requirements including diversion rate, Green Halo, and ordinances
 */

import type { CountyRequirements as CountyRequirementsType } from '../../types/county';

interface CountyRequirementsProps {
  requirements: CountyRequirementsType;
  countyName: string;
}

export function CountyRequirements({ requirements, countyName }: CountyRequirementsProps) {
  return (
    <section id="requirements" className="py-12 border-b border-neutral-200">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl font-bold text-neutral-900 mb-6">
          Local Requirements
        </h2>

        <div className={`grid grid-cols-1 ${requirements.greenHaloThreshold ? 'md:grid-cols-2' : ''} gap-6 mb-8`}>
          {/* Diversion Rate Card */}
          <div className="bg-white border-2 border-primary-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-2xl">🎯</span>
              Required Diversion Rate
            </h3>
            <div className="mb-4">
              <div className="text-5xl font-bold text-primary-600 mb-2">
                {requirements.diversionRate}%
              </div>
              <p className="text-sm text-neutral-600">
                Minimum waste diversion from landfills required for projects in {countyName} County
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> This includes recycling, reuse, salvage, and other approved diversion methods.
                Weight tickets from approved facilities required for verification.
              </p>
            </div>
          </div>

          {/* Green Halo Card - Only show if required */}
          {requirements.greenHaloThreshold && (
            <div className="bg-white border-2 border-neutral-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <span className="text-2xl">♻️</span>
                Green Halo Program
              </h3>
              <div className="mb-4">
                <div className="text-sm uppercase tracking-wide text-neutral-500 mb-1">
                  Tonnage Threshold
                </div>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {requirements.greenHaloThreshold.toLocaleString()} tons
                </div>
                <p className="text-sm text-neutral-600">
                  Projects generating more than this amount require Green Halo certification
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-900">
                  <strong>Required:</strong> Enhanced tracking and reporting through the Green Halo program for large projects.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Local Ordinances */}
        {requirements.localOrdinances && requirements.localOrdinances.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-neutral-900 mb-4">
              Local Ordinances & Additional Requirements
            </h3>
            <div className="space-y-4">
              {requirements.localOrdinances.map((ordinance, index) => (
                <div
                  key={index}
                  className="border-l-4 border-primary-500 pl-4 py-2"
                >
                  <h4 className="font-semibold text-neutral-900 mb-2">
                    {ordinance.title}
                  </h4>
                  <p className="text-sm text-neutral-700 mb-2">
                    {ordinance.description}
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {ordinance.effectiveDate && (
                      <span className="text-neutral-500">
                        Effective: {new Date(ordinance.effectiveDate).toLocaleDateString()}
                      </span>
                    )}
                    {ordinance.link && (
                      <a
                        href={ordinance.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View Full Ordinance →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Notes */}
        {requirements.additionalNotes && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-5">
            <h4 className="font-semibold text-yellow-900 mb-2">Additional Information</h4>
            <p className="text-sm text-yellow-800 whitespace-pre-line">
              {requirements.additionalNotes}
            </p>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-neutral-50 rounded-lg p-6">
          <h4 className="font-semibold text-neutral-900 mb-3">Need Help Understanding Requirements?</h4>
          <p className="text-sm text-neutral-700 mb-4">
            Requirements can vary based on project type, size, and scope. We recommend contacting your local
            building department for project-specific guidance.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="/docs/calgreen-overview"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Learn About CalGreen →
            </a>
            <a
              href="/docs/faq"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View FAQ →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
